import { useGtm as useVueGtm } from '@gtm-support/vue-gtm'
import { v4 as uuidv4 } from 'uuid';
import { track } from '@builder.io/sdk-vue'


/**
 * Get the value of a specified cookie
 * @param {string} name - The name of the cookie
 * @returns {string|null} The value of the cookie, or null if the cookie does not exist
 */
function getCookie(name) {
    const value = "; " + document.cookie;
    const parts = value.split("; " + name + "=");
    if (parts.length == 2) return parts.pop().split(";").shift();
}

/**
 * Track an event using Builder.io SDK
 * @param {string} eventName - The name of the event to track
 * @returns {Promise} A promise that resolves when the event is tracked
 */
export function builderTrackEvent(eventName) {
    return track({
        type: eventName,
        apiKey: '16799f3421e14584be07999a4c604287'
    });
}

/**
 * Prepare the 'add_to_cart' event data for Google Tag Manager
 * @param {Object} item - The item being added to cart
 * @returns {Object} The event data
 */
function prepAtc(item) {
    const productID = item.id ?? null;
    const productName = item.title ?? null;
    const productPrice = item.price ?? null;
    const productQuantity = item.qty ?? null;
    const eventTime = new Date().toISOString();
    const gaID = getCookie('_ga');
    const eventID = uuidv4();
    const currentPage = window.location.pathname;
    const value = productPrice * productQuantity ?? 0;

    const eventData = {
        'event': 'addToCart',
        'event_id': eventID,
        'category': 'Ecommerce',
        'action': 'Add to Cart',
        'ecommerce': {
            'currencyCode': 'USD',
            'add': {
                'actionField': {
                    'list': currentPage,
                    'action': 'add'
                },
                'products': [{
                    'id': productID,
                    'name': productName,
                    'price': productPrice,
                    'quantity': productQuantity
                }]
            }
        },
        'value': value,
        'event_time': eventTime,
        'marketing': {
            '_ga': gaID
        }
    };

    return eventData;
}


/**
 * Prepare the 'begin_checkout' event data for Google Tag Manager
 * @param {Array} items - The items being checked out
 * @returns {Object} The event data
 */
function prepCheckoutItems(items) {
    const eventTime = new Date().toISOString();
    const gaID = getCookie('_ga');
    const eventID = uuidv4();
    const currentPage = window.location.pathname;
    const arrayOfLineItems = items.map(item => ({
        'id': item.id,
        'name': item.title,
        'price': item.price,
        'quantity': item.qty
    }));

    const value = arrayOfLineItems.reduce((total, item) =>
        total + item.price * item.quantity, 0);

    const checkoutEventData = {
        'event': 'begin_checkout',
        'event_id': eventID, // The unique ID for this checkout event
        'category': 'Ecommerce',
        'action': 'Checkout',
        'ecommerce': {
            'currencyCode': 'USD',
            'checkout': {
                'actionField': {
                    'list': currentPage, // The page from which the checkout was initiated
                    'action': 'checkout',
                    'step': 1, // The checkout step (optional)
                },
                'products': arrayOfLineItems
            }
        },
        'value': value, // Total value of the items in the cart
        'event_time': eventTime, // The time of the event
        'marketing': {
            '_ga': gaID // The Google Analytics ID
        }
    };

    return checkoutEventData;
}


/**
 * Send an 'add_to_cart' event to Google Tag Manager
 * @param {Object} item - The item being added to cart
 */
export const trackAdd = (item) => {
    const gtm = useVueGtm();
    const eventData = prepAtc(item);
    log('add:eventData', eventData)
    gtm.trackEvent(eventData);
    builderTrackEvent('add_to_cart');
    trackAxonAddToCart(item);
};


/**
 * Send a 'begin_checkout' event to Google Tag Manager
 * @param {Array} items - The items being checked out
 */
export const trackCheckout = (items) => {
    const cookieMarker = useCookie('_lb_sp', { path: '/', maxAge: 60 * 60 * 24 * 7, sameSite: 'Lax', domain: '.lifeboostcoffee.com' });
    cookieMarker.value = window.location.pathname;
    log('cookieMarker', cookieMarker.value)
    const gtm = useVueGtm();
    const eventData = prepCheckoutItems(items);
    log('checkout:eventData', eventData)
    gtm.trackEvent(eventData);
    builderTrackEvent('initiate_checkout');
};


/**
 * Pushes an event to the data layer.
 * @param {string} eventType - The type of event.
 * @param {object} offerDetails - The details of the offer.
 * @param {string} offerDetails.id - The ID of the offer.
 * @param {string} offerDetails.name - The name of the offer.
 */
const pushToDataLayer = (eventType, offerDetails) => {
    if (!offerDetails || !offerDetails.id || !offerDetails.name) {
      console.error('Invalid offer details provided for', eventType);
      return;
    }
  
    window.dataLayer.push({
      'event': eventType,
      'offer_id': offerDetails.id,
      'offer_name': offerDetails.name
    });
  };
  
/**
 * Object containing tracking functions for offers.
 * @namespace trackOffer
 */
export const trackOffer = {
    /**
     * Function to track the view event of an offer.
     * @memberof trackOffer
     * @param {Object} offerDetails - Details of the offer being viewed.
     */
    view: (offerDetails) => pushToDataLayer('view_upsell', offerDetails),

    /**
     * Function to track the accept event of an offer.
     * @memberof trackOffer
     * @param {Object} offerDetails - Details of the offer being accepted.
     */
    accept: (offerDetails) => pushToDataLayer('accept_upsell', offerDetails),

    /**
     * Function to track the decline event of an offer.
     * @memberof trackOffer
     * @param {Object} offerDetails - Details of the offer being declined.
     */
    decline: (offerDetails) => pushToDataLayer('decline_upsell', offerDetails)
};


const trackAxonAddToCart = (item) => {
    axon("track", "add_to_cart",
        {
          "currency": "USD",
           "value": item.price,
           "items": [{
            "item_id": item.id,
            "item_name": item.title,
            "price": item.price,
            "quantity": item.qty,
            "item_category_id": 1868,
            "item_brand": "Lifeboost"
           }]
        }
      )
}

export const trackAxonViewItem = (items) => {
    if (!Array.isArray(items) || !items.length) return;
    
    const viewItems = items.map(item => ({
        // Required fields
        item_id: item.id,
        item_name: item.title,
        price: item.price,
        quantity: item.qty,

        // Recommended fields
        item_category_id: 1868,
        item_brand: "Lifeboost"
        }));

    // Calculate total value for all items
    const totalValue = viewItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    // Construct the view_items event
    const viewItemsEvent = {
        currency: "USD",
        value: totalValue,
        items: viewItems
    };

    // Track in AXON
    axon("track", "view_item", viewItemsEvent);
}
